import React, { createContext, useContext } from 'react';
import { ListWithPagination } from '@anytag/shared-molecules';
import { InfluencerSearchSortField, ReadPackageAccount } from '@anytag/graphql';
import { useInfluencerParamsSearch } from '../hooks';
import { InfluencersContextReturnType } from '../schemaTypes';
import { useInfluencerPackagesList } from './hooks';
import {
  InfluencerPackagesProps,
  InfluencerPackagesFilterFormValues,
  InfluencerPackagesSearchSchemaType
} from './schemaTypes';

type ContextType = InfluencersContextReturnType<
  ReadPackageAccount,
  InfluencerPackagesFilterFormValues,
  InfluencerSearchSortField
> &
  Pick<InfluencerPackagesProps, 'getInfluencersListSelectButtonProps'>;

const Context = createContext<ContextType>({} as ContextType);

interface Props {
  value: InfluencerPackagesProps;
  children: React.ReactElement | Array<React.ReactElement>;
}

export const InfluencerPackagesProvider = ({
  children,
  value: {
    engagementId,
    isPackagePage,
    isTtcmCampaign,
    isEngagementPage,
    getSortSelectButtonProps,
    getListSelectButtonProps,
    getInfluencersListSelectButtonProps
  }
}: Props) => {
  const paramsSearch = useInfluencerParamsSearch<InfluencerPackagesSearchSchemaType>();
  const { page, limit, filter } = paramsSearch;
  const listProps = useInfluencerPackagesList({ page, limit, filter, engagementId });

  return (
    <Context.Provider
      value={{
        ...paramsSearch,
        ...listProps,
        isTtcmCampaign,
        isEngagementPage,
        getListSelectButtonProps,
        getInfluencersListSelectButtonProps,
        sortSelectButtonProps: getSortSelectButtonProps?.(listProps.listRecords, listProps.totalRecords)
      }}
    >
      <ListWithPagination pagination={{ totalRecords: listProps.totalRecords }}>{children}</ListWithPagination>
    </Context.Provider>
  );
};

export const useInfluencerPackagesContext = () => useContext<ContextType>(Context);
